import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarDays, ClipboardList, CreditCard, Users, Wallet } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

const homeCards = [
  {
    label: 'Students',
    path: '/modules/students/classes',
    icon: Users,
    gradient: 'from-indigo-600 via-purple-600 to-violet-700',
    note: 'Profiles, batches, dropped, enquiries',
  },
  {
    label: 'Exam',
    path: '/modules/exams/classes',
    icon: ClipboardList,
    gradient: 'from-rose-600 via-red-600 to-orange-700',
    note: 'Marks, totals, ranking by batch',
  },
  {
    label: 'Payments',
    path: '/modules/payments/classes',
    icon: CreditCard,
    gradient: 'from-amber-500 via-orange-600 to-amber-700',
    note: 'Paid, due, invoices, history',
  },
  {
    label: 'Attendance',
    path: '/modules/attendance/classes',
    icon: CalendarDays,
    gradient: 'from-cyan-600 via-teal-600 to-emerald-700',
    note: 'Daily marking and student calendar',
  },
];

export default function DashboardPage() {
  const { data: expenseSummary } = useQuery({
    queryKey: ['expense-summary'],
    queryFn: async () => {
      try {
        const res = await api.get<any>('/api/expenses/summary');
        return res.data;
      } catch {
        return null;
      }
    },
  });

  return (
    <div className="min-h-full pb-4">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-5 sm:p-7 overflow-hidden relative shadow-2xl border border-slate-700/50">
        <div className="absolute -right-16 -top-12 h-44 w-44 rounded-full bg-purple-500/20 blur-2xl" />
        <div className="absolute -left-10 bottom-0 h-28 w-28 rounded-full bg-indigo-500/15 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-4 mb-3">
            <img src="/paathshala-logo.png" alt="Paathshaala" className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-contain flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-indigo-200/80">Paathshaala command center</p>
              <h1 className="mt-1 text-2xl sm:text-4xl font-black leading-tight">What do you want to manage?</h1>
            </div>
          </div>
          <p className="text-sm sm:text-base text-white/70 max-w-xl">Choose one flow, then pick class, batch/status, and student. Built for fast phone use.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {homeCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
            >
              <Link
                to={card.path}
                className={`group block rounded-2xl bg-gradient-to-br ${card.gradient} p-5 min-h-[156px] text-white shadow-xl active:scale-[0.985] transition-all hover:-translate-y-0.5 overflow-hidden relative`}
              >
                <div className="absolute -right-8 -bottom-10 h-32 w-32 rounded-full bg-white/15" />
                <div className="relative flex h-full flex-col justify-between gap-8">
                  <div className="flex items-start justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-white/18 flex items-center justify-center backdrop-blur-sm">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      <ArrowRight className="h-5 w-5" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-3xl font-black tracking-tight">{card.label}</h2>
                    <p className="text-sm text-white/75 mt-1">{card.note}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Daily Expenses Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.24 }}
        className="mt-3"
      >
        <Link
          to="/expenses"
          className="group block rounded-2xl bg-gradient-to-br from-rose-500 via-rose-600 to-pink-700 p-5 min-h-[120px] text-white shadow-xl active:scale-[0.985] transition-all hover:-translate-y-0.5 overflow-hidden relative"
        >
          <div className="absolute -right-8 -bottom-10 h-32 w-32 rounded-full bg-white/15" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-white/18 flex items-center justify-center backdrop-blur-sm">
                <Wallet className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-black tracking-tight">Daily Expenses</h2>
                <p className="text-xs text-white/75 mt-0.5">Track spending by category</p>
              </div>
            </div>
            <div className="text-right">
              {expenseSummary && (
                <>
                  <p className="text-[10px] text-white/70 uppercase tracking-wider">Today</p>
                  <p className="text-lg font-black">{formatCurrency(expenseSummary.today || 0)}</p>
                  <p className="text-[10px] text-white/70">Month: {formatCurrency(expenseSummary.thisMonth || 0)}</p>
                </>
              )}
            </div>
            <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-1 transition-transform ml-3">
              <ArrowRight className="h-5 w-5" />
            </div>
          </div>
        </Link>
      </motion.div>
    </div>
  );
}
